/**
 * Week ranges and the range summary (plan §3.1, §5, §6) — one implementation for the Tracker, Reports and the
 * Board, so the three can never disagree about which weeks a period means or what its Total is.
 *
 * - **Total / Average / Latest** are universal: the sum, the mean and the most recent of the *non-null numeric*
 *   weekly values in the inclusive From→To range, identical for every KPI (manual or calculated, absolute or
 *   cumulative). A blank week is skipped — never counted as zero — and a range with no value answers `null`.
 * - A custom range may span at most **104 reporting weeks**; only the explicit "All weeks" period is exempt.
 *   A reversed or over-cap range is **rejected**, never swapped or clamped.
 * - **Week-over-week movement** is neutral (`↑ ↓ —`). The % change exists only when the previous value is a
 *   real non-zero number; a missing value on either side means "no comparison", never zero.
 */
import { currentReportingSunday } from './week-calendar.js';
/** The largest span, in reporting weeks, a custom range may cover. "All weeks" is a separate, explicit period. */
export const MAX_RANGE_WEEKS = 104;
export const LAST_N_WEEK_OPTIONS = [13, 26];
/** The exact user-facing reasons a range is refused; the API answers with these verbatim. */
export const WEEK_RANGE_MESSAGES = {
    NO_WEEKS: 'There are no reporting weeks yet.',
    UNKNOWN_WEEK: 'Select a valid From and To week.',
    REVERSED: 'From week must be on or before To week — the range was rejected, not swapped.',
    OVER_CAP: `The range spans more than ${MAX_RANGE_WEEKS} weeks — it was rejected, not shortened. Narrow it or use All weeks.`,
};
// ISO dates sort as strings.
const byDate = (a, b) => a.weekDate.localeCompare(b.weekDate);
/** A copy of `weeks` in date order — callers may pass them in any order. */
export const sortWeeks = (weeks) => [...weeks].sort(byDate);
const refuse = (error) => ({ ok: false, error, message: WEEK_RANGE_MESSAGES[error] });
const accept = (weeks, all = false) => ({
    ok: true,
    weeks,
    fromWeekId: weeks[0].id,
    toWeekId: weeks[weeks.length - 1].id,
    all,
});
/**
 * The inclusive custom range From→To. Refused when either id is unknown, when From is after To, or when the
 * span exceeds `MAX_RANGE_WEEKS` reporting weeks.
 */
export const weeksBetween = (weeks, fromWeekId, toWeekId) => {
    const sorted = sortWeeks(weeks);
    const fromIndex = sorted.findIndex((week) => week.id === fromWeekId);
    const toIndex = sorted.findIndex((week) => week.id === toWeekId);
    if (fromIndex === -1 || toIndex === -1) {
        return refuse('UNKNOWN_WEEK');
    }
    if (fromIndex > toIndex) {
        return refuse('REVERSED');
    }
    if (toIndex - fromIndex + 1 > MAX_RANGE_WEEKS) {
        return refuse('OVER_CAP');
    }
    return accept(sorted.slice(fromIndex, toIndex + 1));
};
/**
 * The week the relative periods end on: the current reporting week (the Sunday on or before today in Amman) when
 * it was generated; otherwise the latest week before it; otherwise — today precedes every week — the first week.
 * Returns its index in `sorted`, or -1 when there are no weeks.
 */
export const anchorWeekIndex = (sorted, now) => {
    const sunday = currentReportingSunday(now);
    let anchor = -1;
    sorted.forEach((week, index) => {
        if (week.weekDate <= sunday) {
            anchor = index;
        }
    });
    return anchor === -1 && sorted.length > 0 ? 0 : anchor;
};
const yearOf = (week) => week.weekDate.slice(0, 4);
const quarterOf = (week) => `${yearOf(week)}-Q${Math.floor((Number(week.weekDate.slice(5, 7)) - 1) / 3) + 1}`;
/** From the first week sharing `key` with the anchor, through the anchor — never into another year or quarter. */
const sameKeyThroughAnchor = (sorted, anchor, key) => {
    const anchorKey = key(sorted[anchor]);
    let from = anchor;
    while (from > 0 && key(sorted[from - 1]) === anchorKey) {
        from -= 1;
    }
    return sorted.slice(from, anchor + 1);
};
/**
 * Resolves a `period` to its concrete weeks (plan §5), so the Tracker, Reports and the Board share one answer:
 * - `YTD` — the first reporting week of the anchor's calendar year through the anchor week;
 * - `QUARTER` — the first reporting week of the anchor's calendar quarter through the anchor week;
 * - `LAST_N` — the `n` weeks ending at the anchor (fewer when fewer exist);
 * - `ALL` — every week, the one period exempt from the cap;
 * - `CUSTOM` — `weeksBetween`, with its rejections.
 * The anchor is described at `anchorWeekIndex`. `now` is an instant; "today" is taken in Asia/Amman.
 */
export const resolveWeekRange = (period, weeks, now) => {
    const sorted = sortWeeks(weeks);
    if (sorted.length === 0) {
        return refuse('NO_WEEKS');
    }
    const anchor = anchorWeekIndex(sorted, now);
    switch (period.kind) {
        case 'ALL':
            return accept(sorted, true);
        case 'CUSTOM':
            return weeksBetween(sorted, period.fromWeekId, period.toWeekId);
        case 'LAST_N':
            return accept(sorted.slice(Math.max(0, anchor - period.n + 1), anchor + 1));
        case 'QUARTER':
            return accept(sameKeyThroughAnchor(sorted, anchor, quarterOf));
        case 'YTD':
            return accept(sameKeyThroughAnchor(sorted, anchor, yearOf));
    }
};
/** The ids of a relative period's first and last week, for pre-filling From/To selects; `null` with no weeks. */
export const periodWeekIds = (period, weeks, now) => {
    const range = resolveWeekRange(period, weeks, now);
    return range.ok ? { fromId: range.fromWeekId, toId: range.toWeekId } : null;
};
export const yearToDateWeekIds = (weeks, now) => periodWeekIds({ kind: 'YTD' }, weeks, now);
export const currentQuarterWeekIds = (weeks, now) => periodWeekIds({ kind: 'QUARTER' }, weeks, now);
export const lastNWeekIds = (weeks, n, now) => periodWeekIds({ kind: 'LAST_N', n }, weeks, now);
const isRealNumber = (value) => typeof value === 'number' && Number.isFinite(value);
const numericValues = (weekIds, valueAt) => weekIds.map(valueAt).filter(isRealNumber);
/** Sum of the non-null numeric values of the weeks; `null` (never 0) when there are none. */
export const computeRangeTotal = (weekIds, valueAt) => {
    const values = numericValues(weekIds, valueAt);
    return values.length === 0 ? null : values.reduce((sum, value) => sum + value, 0);
};
/** Mean of the non-null numeric values: blank weeks leave both the sum and the divisor; a real 0 counts. */
export const computeRangeAverage = (weekIds, valueAt) => {
    const values = numericValues(weekIds, valueAt);
    return values.length === 0 ? null : values.reduce((sum, value) => sum + value, 0) / values.length;
};
/** The value of the latest week (by the order given, oldest first) that has a numeric value; `null` when none. */
export const computeRangeLatest = (weekIds, valueAt) => {
    for (let index = weekIds.length - 1; index >= 0; index -= 1) {
        const value = valueAt(weekIds[index]);
        if (isRealNumber(value)) {
            return value;
        }
    }
    return null;
};
/** All three at once, reading each week's value exactly once. */
export const summarizeRange = (weekIds, valueAt) => {
    const series = weekIds.map(valueAt);
    const positions = series.map((_value, index) => String(index));
    const atPosition = (position) => series[Number(position)];
    return {
        total: computeRangeTotal(positions, atPosition),
        average: computeRangeAverage(positions, atPosition),
        latest: computeRangeLatest(positions, atPosition),
    };
};
/**
 * `kpiId → RangeSummary` over one range, memoised per KPI. `valueAt` must already resolve a calculated KPI's own
 * weekly series (Phase 3); the summary then reduces it exactly like a manual KPI's stored values.
 */
export const buildRangeSummaryResolver = (weekIds, valueAt) => {
    const cache = new Map();
    return (kpiId) => {
        let summary = cache.get(kpiId);
        if (!summary) {
            summary = summarizeRange(weekIds, (weekId) => valueAt(kpiId, weekId));
            cache.set(kpiId, summary);
        }
        return summary;
    };
};
/** Neutral glyphs: movement is information, never good or bad. */
export const MOVEMENT_GLYPHS = { UP: '↑', DOWN: '↓', FLAT: '—' };
/** A change smaller than this many percent (or units, when there is no % to compare) reads as flat. */
export const FLAT_THRESHOLD = 1;
/**
 * Week-over-week movement of `current` against `previous`. `null` — no comparison — unless both are real numbers;
 * a missing week is never treated as zero.
 */
export const weekOverWeek = (current, previous) => {
    if (!isRealNumber(current) || !isRealNumber(previous)) {
        return null;
    }
    const change = current - previous;
    const changePct = previous === 0 ? null : (change / Math.abs(previous)) * 100;
    const magnitude = Math.abs(changePct ?? change);
    const direction = magnitude < FLAT_THRESHOLD ? 'FLAT' : change > 0 ? 'UP' : 'DOWN';
    return { direction, change, changePct };
};
//# sourceMappingURL=range-summary.js.map